import {HELPER_OBJECTS_LAYER} from "@app/template-editor/helpers/helper-objects"
import {ThreeAnnotationBase} from "@app/template-editor/helpers/three-annotation-base"
import {getNextThreeObjectPart} from "@app/template-editor/helpers/three-object"
import {Three as THREE} from "@cm/material-nodes/three"

const _vector = new THREE.Vector3()
const _viewMatrix = new THREE.Matrix4()
const _viewProjectionMatrix = new THREE.Matrix4()
const _a = new THREE.Vector3()
const _b = new THREE.Vector3()

export class ThreeAnnotationRenderer {
    private _width = 0
    private _height = 0
    private _widthHalf = 0
    private _heightHalf = 0
    private distanceCache = new WeakMap<ThreeAnnotationBase, number>()
    private raycaster = new THREE.Raycaster()

    domElement: HTMLElement

    constructor(
        parameters: {
            element?: HTMLElement
        } = {},
    ) {
        const domElement = parameters.element !== undefined ? parameters.element : document.createElement("div")
        domElement.style.overflow = "hidden"
        this.domElement = domElement

        this.raycaster.layers.enable(HELPER_OBJECTS_LAYER)
    }

    getSize() {
        return {
            width: this._width,
            height: this._height,
        }
    }

    render(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
        if (scene.matrixWorldAutoUpdate === true) scene.updateMatrixWorld()
        if (camera.parent === null && camera.matrixWorldAutoUpdate === true) camera.updateMatrixWorld()

        _viewMatrix.copy(camera.matrixWorldInverse)
        _viewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, _viewMatrix)

        const visited = new Set<HTMLElement>()

        scene.traverseVisible((object) => {
            if (!(object instanceof ThreeAnnotationBase)) return

            this.distanceCache.set(object, this.getDistanceToSquared(camera, object))

            _vector.setFromMatrixPosition(object.matrixWorld)
            _vector.applyMatrix4(_viewProjectionMatrix)

            const element = object.getElement(camera)
            visited.add(element)

            const inFrustum = _vector.z >= -1 && _vector.z <= 1 && object.layers.test(camera.layers) === true

            if (!inFrustum) {
                element.style.visibility = "hidden"
                return
            }

            if (element.parentNode !== this.domElement) {
                this.domElement.appendChild(element)
            }

            const x = _vector.x * this._widthHalf + this._widthHalf
            const y = -_vector.y * this._heightHalf + this._heightHalf

            const {x: offsetX, y: offsetY} = object.getTranslationOffset(element)
            const cx = x + offsetX
            const cy = y + offsetY

            const centerX = cx + element.clientWidth / 2
            const centerY = cy + element.clientHeight / 2

            const onScreen = centerX >= 0 && centerX <= this._width && centerY >= 0 && centerY <= this._height

            if (!onScreen) {
                element.style.visibility = "hidden"
                return
            }

            element.style.visibility = "visible"
            element.style.transform = `translate(${cx}px, ${cy}px)`
            element.style.opacity = `${this.opacityForObject(object, scene, camera)}`
        })

        for (const child of Array.from(this.domElement.children)) {
            if (!visited.has(child as HTMLElement)) {
                this.domElement.removeChild(child)
            }
        }

        this.zOrder(scene, camera)
    }

    setSize(width: number, height: number) {
        this._width = width
        this._height = height

        this._widthHalf = this._width / 2
        this._heightHalf = this._height / 2

        this.domElement.style.width = width + "px"
        this.domElement.style.height = height + "px"
    }

    private opacityForObject(object: ThreeAnnotationBase, scene: THREE.Scene, camera: THREE.PerspectiveCamera): number {
        if (!object.isConcealable() || object.getOcclusionOpacity() === 1.0) return 1.0

        _a.setFromMatrixPosition(object.matrixWorld)
        _b.setFromMatrixPosition(camera.matrixWorld)
        const cameraToObject = _a.sub(_b)
        const distanceToCamera = cameraToObject.length()
        const dir = cameraToObject.normalize()

        this.raycaster.set(_b, dir)

        const firstIntersection = this.raycaster.intersectObjects(scene.children, true).find((intersection) => {
            if (!intersection.object.visible) return false
            if (intersection.distance < camera.near || intersection.distance > camera.far) return false

            const {object} = intersection
            const threeObjectPart = getNextThreeObjectPart(object)
            if (threeObjectPart) return true

            return false
        })

        if (!firstIntersection || firstIntersection.distance > distanceToCamera) return 1.0

        return object.getOcclusionOpacity()
    }

    private getDistanceToSquared(object1: THREE.Object3D, object2: THREE.Object3D) {
        _a.setFromMatrixPosition(object1.matrixWorld)
        _b.setFromMatrixPosition(object2.matrixWorld)

        return _a.distanceToSquared(_b)
    }

    private filterAndFlatten(scene: THREE.Scene) {
        const result: ThreeAnnotationBase[] = []

        scene.traverse(function (object) {
            if (object instanceof ThreeAnnotationBase) result.push(object)
        })

        return result
    }

    private zOrder(scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
        const sorted = this.filterAndFlatten(scene).sort((a, b) => {
            if (a.renderOrder !== b.renderOrder) {
                return b.renderOrder - a.renderOrder
            }

            const distanceA = this.distanceCache.get(a)
            const distanceB = this.distanceCache.get(b)

            if (distanceA === undefined) return 1
            if (distanceB === undefined) return -1

            return distanceA - distanceB
        })

        const zMax = sorted.length

        for (let i = 0, l = sorted.length; i < l; i++) {
            sorted[i].getElement(camera).style.zIndex = `${zMax - i}`
        }
    }
}
